/**
 * Wishlist Manager - LocalStorage (Temporário)
 * Gerencia wishlist usando localStorage do navegador
 * Não requer login (solução paliativa)
 * 
 * TODO: Migrar para Customer Metafields quando backend estiver pronto
 */

class WishlistManager {
  constructor() {
    this.storageKey = 'crown-wishlist';
    this.wishlist = this.loadFromStorage();
    this.init();
  }

  init() {
    console.log('Wishlist Manager inicializado (localStorage)', {
      itemCount: this.wishlist.length
    });
    
    // Inicializa os event listeners
    this.initEventListeners();
    
    // Atualiza estado dos botões na página
    this.updateButtonStates();
    
    // Dispara evento de wishlist carregada
    window.dispatchEvent(new CustomEvent('wishlist-loaded', { 
      detail: { wishlist: this.wishlist } 
    }));
  }

  updateButtonStates() {
    // Atualiza o estado visual de todos os botões na página
    const buttons = document.querySelectorAll('[data-wishlist-toggle]');
    
    buttons.forEach(button => {
      const productHandle = button.dataset.productHandle;
      const isInWishlist = this.isInWishlist(productHandle);
      
      button.classList.toggle('active', isInWishlist);
      button.setAttribute('aria-pressed', isInWishlist);
    });
  }

  loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Erro ao carregar wishlist do localStorage:', error);
      return [];
    }
  }

  saveToStorage() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
      return true;
    } catch (error) {
      console.error('Erro ao salvar wishlist no localStorage:', error);
      return false;
    }
  }

  isLoggedIn() {
    // Sempre retorna true para não bloquear (localStorage não precisa login)
    return true;
  }

  async updateWishlist() {
    // Salva no localStorage
    return this.saveToStorage();
  }

  async addProduct(productHandle) {
    if (!this.wishlist.includes(productHandle)) {
      this.wishlist.push(productHandle);
      
      const success = await this.updateWishlist();
      
      if (success) {
        window.dispatchEvent(new CustomEvent('wishlist-updated', { 
          detail: { wishlist: this.wishlist, action: 'add', productHandle } 
        }));
        
        // Feedback visual
        this.showNotification('Produto adicionado à lista de desejos!');
        
        return true;
      }
    } else {
      this.showNotification('Produto já está na lista de desejos');
    }
    
    return false;
  }

  async removeProduct(productHandle) {
    const index = this.wishlist.indexOf(productHandle);
    
    if (index > -1) {
      this.wishlist.splice(index, 1);
      
      const success = await this.updateWishlist();
      
      if (success) {
        window.dispatchEvent(new CustomEvent('wishlist-updated', { 
          detail: { wishlist: this.wishlist, action: 'remove', productHandle } 
        }));
        
        // Feedback visual
        this.showNotification('Produto removido da lista de desejos');
        
        return true;
      }
    }
    
    return false;
  }

  async toggleProduct(productHandle) {
    if (this.wishlist.includes(productHandle)) {
      return await this.removeProduct(productHandle);
    } else {
      return await this.addProduct(productHandle);
    }
  }

  isInWishlist(productHandle) {
    return this.wishlist.includes(productHandle);
  }

  getWishlist() {
    return [...this.wishlist];
  }

  getCount() {
    return this.wishlist.length;
  }

  async getProductsDetails() {
    const productHandles = this.getWishlist();
    
    if (productHandles.length === 0) {
      return [];
    }

    try {
      const productsData = await Promise.all(
        productHandles.map(handle => 
          fetch(`/products/${handle}.js`)
            .then(res => res.json())
            .catch(err => {
              console.error(`Erro ao carregar produto ${handle}:`, err);
              return null;
            })
        )
      );
      
      return productsData.filter(product => product !== null);
    } catch (error) {
      console.error('Erro ao carregar detalhes dos produtos:', error);
      return [];
    }
  }

  showNotification(message) {
    // Cria notificação temporária
    const notification = document.createElement('div');
    notification.className = 'wishlist-notification';
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: #1f1f1f;
      color: white;
      padding: 16px 24px;
      border-radius: 4px;
      z-index: 10000;
      animation: slideIn 0.3s ease;
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  initEventListeners() {
    // Listener para botões de wishlist
    document.addEventListener('click', async (e) => {
      const wishlistBtn = e.target.closest('[data-wishlist-toggle]');
      const wishlistRemove = e.target.closest('[data-wishlist-remove]');
      
      if (wishlistBtn) {
        e.preventDefault();
        
        const productHandle = wishlistBtn.dataset.productHandle || wishlistBtn.dataset.productId;
        
        if (!productHandle) {
          console.error('Product handle não encontrado');
          return;
        }
        
        // Toggle loading state
        wishlistBtn.classList.add('loading');
        wishlistBtn.disabled = true;
        
        const success = await this.toggleProduct(productHandle);
        
        wishlistBtn.classList.remove('loading');
        wishlistBtn.disabled = false;
        
        if (success || true) {
          // Update button state
          const isInWishlist = this.isInWishlist(productHandle);
          wishlistBtn.classList.toggle('active', isInWishlist);
          wishlistBtn.setAttribute('aria-pressed', isInWishlist);
        }
      }
      
      if (wishlistRemove) {
        e.preventDefault();
        
        const productHandle = wishlistRemove.dataset.productHandle || wishlistRemove.dataset.productId;
        
        if (!productHandle) {
          console.error('Product handle não encontrado');
          return;
        }
        
        wishlistRemove.disabled = true;
        
        await this.removeProduct(productHandle);
        
        wishlistRemove.disabled = false;
      }
    });
  }
}

// Adiciona estilos para notificações
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from {
      transform: translateX(400px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes slideOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(400px);
      opacity: 0;
    }
  }
`;
document.head.appendChild(style);

// Auto-inicializa quando o DOM estiver pronto
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.wishlistManager = new WishlistManager();
  });
} else {
  window.wishlistManager = new WishlistManager();
}
